import { MessageAttachment } from "../models/MessageAttachment";

/** Mirrors Messaging/Model/AttachmentRules.cs: the accepted types per kind and each kind's cap. */
export type AttachmentKind = MessageAttachment["kind"];

const MB = 1024 * 1024;
export const MAX_ATTACHMENTS = 10;

const MAX_BYTES: Record<AttachmentKind, number> = { Image: 10 * MB, File: 25 * MB, Video: 100 * MB };

const IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];
const VIDEO_TYPES = ["video/mp4", "video/webm", "video/quicktime"];
/** Content type by extension, for files the browser reports without one. */
const FILE_TYPES: Record<string, string> = {
  pdf: "application/pdf",
  txt: "text/plain",
  zip: "application/zip",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ppt: "application/vnd.ms-powerpoint",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  mp3: "audio/mpeg",
  m4a: "audio/mp4",
  wav: "audio/wav",
  mid: "audio/midi",
  midi: "audio/midi",
};
const FILE_ALIASES = ["application/x-zip-compressed", "audio/x-m4a", "audio/x-wav", "audio/x-midi"];

/** The `accept` list of the file picker (files and video; photos use `image/*`). */
export const FILE_ACCEPT = [...Object.keys(FILE_TYPES).map((ext) => `.${ext}`), ...VIDEO_TYPES].join(",");

/** The content type to declare: the browser's, or the extension's when the browser gave none. */
export const contentTypeOf = (file: File): string => {
  if (file.type) return file.type.toLowerCase();
  const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
  return FILE_TYPES[extension] ?? "";
};

/** The kind a content type is shown as, or null when it cannot be attached. */
export const kindOf = (contentType: string): AttachmentKind | null => {
  if (IMAGE_TYPES.includes(contentType)) return "Image";
  if (VIDEO_TYPES.includes(contentType)) return "Video";
  if (Object.values(FILE_TYPES).includes(contentType) || FILE_ALIASES.includes(contentType)) return "File";
  return null;
};

export const maxBytesOf = (kind: AttachmentKind): number => MAX_BYTES[kind];

/** A size in the reader's language: "820 kB", "6.2 MB". */
export const formatBytes = (bytes: number, locale: string): string => {
  const [value, unit] = bytes >= MB ? [bytes / MB, "megabyte"] : [Math.max(bytes / 1024, 1), "kilobyte"];
  return new Intl.NumberFormat(locale, { style: "unit", unit, maximumFractionDigits: 1 }).format(value);
};
